import React, { useState, useMemo } from 'react';
import client from '../../../shared/api/client';
import { toastError } from '../../../shared/toastHelper';
import CrmSearchInput from '../../shared/components/CrmSearchInput';
import GivingBadge from '../../shared/components/GivingBadge';
import ActionsMenu from '../../shared/components/ActionsMenu';
import GivingConfirmModal from '../../shared/components/GivingConfirmModal';
import GivingRecurringCancelOptions from '../../shared/components/GivingRecurringCancelOptions';
import { formatCurrency, campaignTypeLabel, CAMPAIGN_STATUS_DISPLAY, campaignStatusDisplay } from '../../shared/givingHelpers';
import '../styles/givingCampaignDashboard.css';

const SortIcon = ({ direction }) => {
  if (!direction) {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        className="gc-sort-icon gc-sort-icon--inactive">
        <path d="M12 5V19M12 5L6 11M12 5L18 11" opacity="0.5" />
        <path d="M12 19V5M12 19L6 13M12 19L18 13" opacity="0.5" />
      </svg>
    );
  }

  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      className="gc-sort-icon gc-sort-icon--active">
      {direction === 'asc'
        ? <path d="M12 19V5M12 5L5 12M12 5L19 12" />
        : <path d="M12 5V19M12 19L5 12M12 19L19 12" />}
    </svg>
  );
};

const STATUS_ORDER = Object.keys(CAMPAIGN_STATUS_DISPLAY);

const formatVariant = (format) => {
  switch (format) {
    case 'form': return 'warning';
    case 'event': return 'purple';
    case 'page': return 'blue';
    default: return 'gray';
  }
};

const SORTABLE_KEYS = ['name', 'status', 'type', 'goal', 'raised', 'progress', 'donors'];

const COLUMNS = [
  { key: 'name', label: 'Campaign', style: { minWidth: 180 } },
  { key: 'format', label: 'Format' },
  { key: 'status', label: 'Status' },
  { key: 'type', label: 'Type' },
  { key: 'goal', label: 'Goal', align: 'right' },
  { key: 'raised', label: 'Raised', align: 'right' },
  { key: 'progress', label: 'Progress', align: 'right' },
  { key: 'donors', label: 'Donors', align: 'right' },
  { key: 'days_left', label: 'Days Left' },
];

const GivingCampaignDashboard = ({ giving_campaigns, team }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);

  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [recurringHandling, setRecurringHandling] = useState(null);

  const pendingRecurringCount = pendingDelete?.active_recurring_donation_count || 0;

  const closeDeleteModal = () => {
    setPendingDelete(null);
    setRecurringHandling(null);
  };

  const confirmDelete = async () => {
    if (deleting || !pendingDelete) return;
    if (pendingRecurringCount > 0 && !recurringHandling) return;
    setDeleting(true);
    try {
      const data = pendingRecurringCount > 0 ? { recurring_donation_handling: recurringHandling } : undefined;
      await client.delete(`/teams/${team.id}/giving/${pendingDelete.id}.json`, { data });
      window.location.reload();
    } catch (err) {
      const errors = err.response?.data?.errors;
      toastError(errors ? errors.join(', ') : 'Could not delete campaign');
      setDeleting(false);
      closeDeleteModal();
    }
  };

  const handleSort = (field) => {
    if (!SORTABLE_KEYS.includes(field)) return;
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredCampaigns = useMemo(() => {
    let result = [...giving_campaigns];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(c =>
        c.name.toLowerCase().includes(term) ||
        c.campaign_code.toLowerCase().includes(term)
      );
    }

    if (statusFilter !== 'all') {
      result = result.filter(c => c.display_status === statusFilter);
    }

    if (typeFilter !== 'all') {
      result = result.filter(c => c.campaign_type === typeFilter);
    }

    result.sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'name': aVal = a.name.toLowerCase(); bVal = b.name.toLowerCase(); break;
        case 'status': aVal = STATUS_ORDER.indexOf(a.display_status); bVal = STATUS_ORDER.indexOf(b.display_status); break;
        case 'type': aVal = a.campaign_type || ''; bVal = b.campaign_type || ''; break;
        case 'goal': aVal = a.goal_amount_cents || 0; bVal = b.goal_amount_cents || 0; break;
        case 'raised': aVal = a.total_raised_cents || 0; bVal = b.total_raised_cents || 0; break;
        case 'progress': aVal = a.progress_percentage || 0; bVal = b.progress_percentage || 0; break;
        case 'donors': aVal = a.donor_count || 0; bVal = b.donor_count || 0; break;
        default: aVal = a.name.toLowerCase(); bVal = b.name.toLowerCase();
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [giving_campaigns, searchTerm, statusFilter, typeFilter, sortField, sortDirection]);

  const uniqueTypes = [...new Set(giving_campaigns.map(c => c.campaign_type).filter(Boolean))];

  return (
    <div>
      <div className="gc-table-wrap">
        <div className="gc-table-toolbar">
          <div className="gc-table-toolbar-search">
            <CrmSearchInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search campaigns..."
              ariaLabel="Search campaigns"
              useDebounce
              shortcutLabel={null}
            />
          </div>
          <div className="d-flex align-items-center gap-2">
            <a href={`/teams/${team.id}/giving/new`} className="btn btn-setlist-primary">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="gc-icon-leading">
                <path d="M12 5V19M5 12H19" />
              </svg>
              <strong>New Campaign</strong>
            </a>
          </div>
        </div>

        <div className="gc-table-filters">
          <div className="d-flex align-items-center flex-wrap gc-filter-row">
            {statusFilter !== 'all' && (
              <div className="gc-filter-chip">
                <button onClick={() => setStatusFilter('all')}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6L6 18M6 6L18 18" />
                  </svg>
                </button>
                <span>Status: {campaignStatusDisplay(statusFilter).label}</span>
              </div>
            )}
            {typeFilter !== 'all' && (
              <div className="gc-filter-chip">
                <button onClick={() => setTypeFilter('all')}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6L6 18M6 6L18 18" />
                  </svg>
                </button>
                <span>Type: {campaignTypeLabel(typeFilter)}</span>
              </div>
            )}
            <div className="gc-filter-trigger">
              <button className="btn btn-setlist-secondary" onClick={() => setFilterMenuOpen(!filterMenuOpen)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="gc-icon-leading">
                  <path d="M12 5V19M5 12H19" />
                </svg>
                Add filter
              </button>
              {filterMenuOpen && (
                <div className="dropdown-menu show gc-filter-menu">
                  <h6 className="dropdown-header">Status</h6>
                  {STATUS_ORDER.map(s => (
                    <a key={s} className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); setStatusFilter(s); setFilterMenuOpen(false); }}>
                      {CAMPAIGN_STATUS_DISPLAY[s].label}
                    </a>
                  ))}
                  <div className="dropdown-divider" />
                  <h6 className="dropdown-header">Type</h6>
                  {uniqueTypes.map(t => (
                    <a key={t} className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); setTypeFilter(t); setFilterMenuOpen(false); }}>
                      {campaignTypeLabel(t)}
                    </a>
                  ))}
                </div>
              )}
            </div>
            {(statusFilter !== 'all' || typeFilter !== 'all') && (
              <button className="btn btn-setlist-secondary" onClick={() => { setStatusFilter('all'); setTypeFilter('all'); }}>
                Clear
              </button>
            )}
          </div>
          <span className="gc-filter-count">
            {filteredCampaigns.length} campaign{filteredCampaigns.length !== 1 ? 's' : ''}
          </span>
        </div>

        <div className="gc-table-scroll">
          <table className="gc-table">
            <thead>
              <tr>
                {COLUMNS.map(col => {
                  const sortable = SORTABLE_KEYS.includes(col.key);
                  const sorted = sortField === col.key;
                  return (
                    <th
                      key={col.key}
                      className={[
                        sortable ? 'gc-th-sortable' : '',
                        col.align === 'right' ? 'gc-th-right' : '',
                      ].filter(Boolean).join(' ')}
                      style={col.style}
                      onClick={() => sortable && handleSort(col.key)}
                    >
                      <span className="gc-th-inner">
                        {col.label}
                        {sortable && (
                          <SortIcon direction={sorted ? sortDirection : null} />
                        )}
                      </span>
                    </th>
                  );
                })}
                <th className="gc-th-right" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {filteredCampaigns.map((campaign, idx) => (
                <tr key={campaign.id}>
                  <td className="gc-td-lead" data-label="Campaign">
                    <a href={`/teams/${team.id}/giving/${campaign.id}`}>
                      {campaign.name}
                    </a>
                    <div className="gc-campaign-code">{campaign.campaign_code}</div>
                  </td>
                  <td data-label="Format">
                    <GivingBadge label={(campaign.format || 'page').charAt(0).toUpperCase() + (campaign.format || 'page').slice(1)} variant={formatVariant(campaign.format)} />
                  </td>
                  <td data-label="Status">
                    <GivingBadge label={campaignStatusDisplay(campaign.display_status).label} variant={campaignStatusDisplay(campaign.display_status).variant} />
                  </td>
                  <td data-label="Type">
                    {campaign.campaign_type ? campaignTypeLabel(campaign.campaign_type) : <span className="gc-td-muted">--</span>}
                  </td>
                  <td className="gc-td-right gc-td-numeric" data-label="Goal">
                    {campaign.goal_amount_cents ? formatCurrency(campaign.goal_amount_cents) : <span className="gc-td-muted">--</span>}
                  </td>
                  <td className="gc-td-right gc-td-numeric" data-label="Raised">
                    {formatCurrency(campaign.total_raised_cents)}
                  </td>
                  <td className="gc-td-right" data-label="Progress">
                    <div className="gc-progress-bar">
                      <div className="gc-progress-track">
                        <div
                          className="gc-progress-fill"
                          style={{ width: `${Math.min(campaign.progress_percentage, 100)}%` }}
                          data-complete={campaign.progress_percentage >= 100 ? 'true' : 'false'}
                        />
                      </div>
                      <span className="gc-progress-label">{campaign.progress_percentage}%</span>
                    </div>
                  </td>
                  <td className="gc-td-right gc-td-numeric" data-label="Donors">
                    {campaign.donor_count}
                  </td>
                  <td data-label="Days Left">
                    {(!campaign.end_date || campaign.format === 'form' || campaign.display_status === 'completed') ? (
                      <span className="gc-td-muted">—</span>
                    ) : campaign.display_status === 'ended' ? (
                      <span className="gc-td-muted">Ended</span>
                    ) : campaign.display_status === 'planned' ? (
                      <span className="gc-td-planned">{campaign.days_remaining}d</span>
                    ) : (
                      <span>{campaign.days_remaining}d</span>
                    )}
                  </td>
                  <td className="gc-td-right" data-label="">
                    <ActionsMenu
                      buttonClassNames={['team-actions-btn-square']}
                      openUpwards={idx === filteredCampaigns.length - 1 && filteredCampaigns.length > 1}
                      actions={[
                        {
                          label: 'Delete',
                          icon: 'fal fa-trash-alt',
                          danger: true,
                          onClick: () => setPendingDelete(campaign),
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
              {filteredCampaigns.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length + 1} className="gc-empty">
                    {giving_campaigns.length === 0 ? (
                      <div>
                        <p className="gc-empty-title">No giving campaigns yet</p>
                        <a href={`/teams/${team.id}/giving/new`} className="btn btn-sm btn-setlist-primary">
                          Create Your First Campaign
                        </a>
                      </div>
                    ) : (
                      <p className="gc-empty-message">No campaigns match your filters</p>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <GivingConfirmModal
        open={!!pendingDelete}
        title="Delete this campaign?"
        body={pendingDelete ? `This removes "${pendingDelete.name}" from your dashboard. Past donations are kept, but the campaign and its public page will no longer be accessible.` : ''}
        confirmLabel="Delete campaign"
        pendingLabel="Deleting..."
        pending={deleting}
        danger
        confirmDisabled={pendingRecurringCount > 0 && !recurringHandling}
        onConfirm={confirmDelete}
        onCancel={() => { if (!deleting) closeDeleteModal(); }}
      >
        <GivingRecurringCancelOptions
          count={pendingRecurringCount}
          amountCents={pendingDelete?.active_recurring_donation_amount_cents}
          value={recurringHandling}
          onChange={setRecurringHandling}
        />
      </GivingConfirmModal>
    </div>
  );
};

export default GivingCampaignDashboard;
